import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { UpsellSurface } from 'fym-shared/src/music24/watermarkUpsell';
import { TypedMemo } from '../../../common/types';
import { watermarkChoiceMessages } from '../choice/watermarkChoiceMessages';
import { useOpenWatermarkOffer } from '../useOpenWatermarkOffer';
import { useUpsellShown } from '../useUpsellShown';
import { useWatermarkSurface } from '../useWatermarkSurface';
import { watermarkFileMessages as messages } from './watermarkFileMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the upsell; each part uses its own slice of it */
import styles from '../WatermarkUpsell.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface WatermarkPostWarningProps {
  /** `post`: the Composer, `count` = the picked channels. `plan`: "Schedule all", `count` = the posts. */
  variant: 'post' | 'plan';
  count: number;
  className?: string;
}

/**
 * The warning line before a post goes out with a watermarked file (design: trial-upsell a5
 * `.tu-warn`). "Remove it first" opens the offer; a member reads the owner note. The post still
 * schedules with one click: the line never blocks.
 */
const WatermarkPostWarningComponent: React.FC<WatermarkPostWarningProps> = ({ variant, count, className }) => {
  const intl = useIntl();
  const { applies, isMember } = useWatermarkSurface();
  const openOffer = useOpenWatermarkOffer();
  const surface: UpsellSurface = 'composer';
  const remove = useCallback(() => openOffer(surface), [openOffer]);
  const shows = applies && (variant === 'post' || count > 0);
  useUpsellShown(surface, shows);
  if (!shows) {
    return null;
  }
  let text = intl.formatMessage(messages.planWarning, { count });
  if (variant === 'post') {
    text =
      count > 0
        ? intl.formatMessage(messages.postWarning, { count })
        : intl.formatMessage(messages.postWarningNoChannel);
  }
  return (
    <div className={clsx(styles.warn, className)} role="note" data-testid={`watermark-post-warning-${variant}`}>
      <span className={styles.warnDot} aria-hidden="true" />
      <span>{withBrandWord(text)}</span>
      {isMember ? (
        <span className={styles.days}>{intl.formatMessage(watermarkChoiceMessages.ownerNote)}</span>
      ) : (
        <button type="button" className={styles.warnLink} onClick={remove}>
          {intl.formatMessage(messages.removeFirst)}
        </button>
      )}
    </div>
  );
};

export const WatermarkPostWarning = TypedMemo(WatermarkPostWarningComponent);
